import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dog,
  Cat,
  Edit,
  X,
  Check,
  Search,
  Loader2,
  Download,
} from 'lucide-react';
import PatientSharing from '../PatientSharing';
import { breedsBySpecies } from './breedData';

const PatientInfoCard = ({
  patient,
  setPatient,
  patientId,
  editing,
  setEditing,
  editForm,
  setEditForm,
  saving,
  handleSaveEdit,
  breedSearch,
  setBreedSearch,
  breedDropdownOpen,
  setBreedDropdownOpen,
  breedRef,
  exportingPdf,
  handleExportPdf,
}) => {
  const editBreeds = breedsBySpecies[patient?.species] || [];
  const filteredEditBreeds = editBreeds.filter(b =>
    b.toLowerCase().includes(breedSearch.toLowerCase())
  );

  const startEditing = () => {
    setEditForm({
      name: patient.name || '',
      breed: patient.breed || '',
      age: patient.age || '',
      weight: patient.weight || '',
      sex: patient.sex || 'unknown',
      owner_name: patient.owner_name || '',
      presenting_complaint: patient.presenting_complaint || ''
    });
    setBreedSearch('');
    setBreedDropdownOpen(false);
    setEditing(true);
  };

  return (
    <Card className="border-[#E5E4E0] bg-white" data-testid="patient-info-card">
      <CardContent className="p-6">
        {editing ? (
          <div className="space-y-4" data-testid="patient-edit-form">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-semibold text-[#2A2C2A] font-['Work_Sans']">Edit Patient Info</h2>
              <Button variant="ghost" size="sm" onClick={() => setEditing(false)} data-testid="cancel-edit-btn">
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-[#5C5F5C] font-medium mb-1 block">Name</label>
                <Input value={editForm.name} onChange={(e) => setEditForm({...editForm, name: e.target.value})} className="border-[#E5E4E0]" data-testid="edit-name" />
              </div>
              <div>
                <label className="text-xs text-[#5C5F5C] font-medium mb-1 block">Breed</label>
                <div className="relative" ref={breedRef}>
                  <div
                    className="flex items-center gap-2 border border-[#E5E4E0] rounded-md px-3 h-10 cursor-pointer focus-within:ring-2 focus-within:ring-[#4B6651]/30 focus-within:border-[#4B6651]"
                    onClick={() => setBreedDropdownOpen(true)}
                    data-testid="edit-breed"
                  >
                    <Search className="w-4 h-4 text-[#5C5F5C] flex-shrink-0" />
                    <input
                      type="text"
                      value={breedDropdownOpen ? breedSearch : editForm.breed}
                      onChange={(e) => {
                        setBreedSearch(e.target.value);
                        setBreedDropdownOpen(true);
                      }}
                      onFocus={() => setBreedDropdownOpen(true)}
                      placeholder="Search breed..."
                      className="flex-1 bg-transparent border-none outline-none text-sm text-[#2A2C2A] placeholder:text-[#5C5F5C]"
                      data-testid="edit-breed-search-input"
                    />
                    {editForm.breed && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditForm({...editForm, breed: ''});
                          setBreedSearch('');
                        }}
                        className="text-[#5C5F5C] hover:text-[#2A2C2A]"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  {breedDropdownOpen && filteredEditBreeds.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-[#E5E4E0] rounded-md shadow-lg max-h-60 overflow-y-auto" data-testid="edit-breed-dropdown">
                      {filteredEditBreeds.map(breed => (
                        <button
                          key={breed}
                          type="button"
                          onClick={() => {
                            setEditForm({...editForm, breed});
                            setBreedSearch('');
                            setBreedDropdownOpen(false);
                          }}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-[#4B6651]/10 transition-colors ${
                            editForm.breed === breed ? 'bg-[#4B6651]/10 text-[#4B6651] font-medium' : 'text-[#2A2C2A]'
                          }`}
                        >
                          {breed}
                        </button>
                      ))}
                    </div>
                  )}
                  {breedDropdownOpen && breedSearch && filteredEditBreeds.length === 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-[#E5E4E0] rounded-md shadow-lg p-4 text-center">
                      <p className="text-sm text-[#5C5F5C]">No breeds matching "{breedSearch}"</p>
                      <button
                        type="button"
                        onClick={() => {
                          setEditForm({...editForm, breed: breedSearch});
                          setBreedDropdownOpen(false);
                          setBreedSearch('');
                        }}
                        className="mt-2 text-sm text-[#4B6651] hover:underline"
                        data-testid="edit-use-custom-breed"
                      >
                        Use "{breedSearch}" as breed
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className="text-xs text-[#5C5F5C] font-medium mb-1 block">Age</label>
                <Input value={editForm.age} onChange={(e) => setEditForm({...editForm, age: e.target.value})} className="border-[#E5E4E0]" data-testid="edit-age" />
              </div>
              <div>
                <label className="text-xs text-[#5C5F5C] font-medium mb-1 block">Weight</label>
                <Input value={editForm.weight} onChange={(e) => setEditForm({...editForm, weight: e.target.value})} className="border-[#E5E4E0]" data-testid="edit-weight" />
              </div>
              <div>
                <label className="text-xs text-[#5C5F5C] font-medium mb-1 block">Sex</label>
                <Select value={editForm.sex} onValueChange={(v) => setEditForm({...editForm, sex: v})}>
                  <SelectTrigger className="border-[#E5E4E0]" data-testid="edit-sex">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male (Intact)</SelectItem>
                    <SelectItem value="female">Female (Intact)</SelectItem>
                    <SelectItem value="castrated">Male (Castrated)</SelectItem>
                    <SelectItem value="spayed">Female (Spayed)</SelectItem>
                    <SelectItem value="unknown">Unknown</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-[#5C5F5C] font-medium mb-1 block">Owner Name</label>
                <Input value={editForm.owner_name} onChange={(e) => setEditForm({...editForm, owner_name: e.target.value})} className="border-[#E5E4E0]" data-testid="edit-owner" />
              </div>
            </div>
            <div>
              <label className="text-xs text-[#5C5F5C] font-medium mb-1 block">Presenting Complaint</label>
              <Textarea value={editForm.presenting_complaint} onChange={(e) => setEditForm({...editForm, presenting_complaint: e.target.value})} className="border-[#E5E4E0] min-h-[80px]" data-testid="edit-complaint" />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSaveEdit} disabled={saving} className="bg-[#4B6651] hover:bg-[#3E5543] text-white" data-testid="save-edit-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                Save Changes
              </Button>
              <Button variant="outline" onClick={() => setEditing(false)} className="border-[#E5E4E0]">Cancel</Button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex flex-col sm:flex-row items-start gap-4">
              <div className={`w-12 h-12 sm:w-16 sm:h-16 rounded-xl flex items-center justify-center flex-shrink-0 ${
                patient.species === 'dog' ? 'bg-[#C06144]/10' : 'bg-[#4B6651]/10'
              }`}>
                {patient.species === 'dog' ? (
                  <Dog className="w-6 h-6 sm:w-8 sm:h-8 text-[#C06144]" />
                ) : (
                  <Cat className="w-6 h-6 sm:w-8 sm:h-8 text-[#4B6651]" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div>
                    <h1 className="text-xl sm:text-2xl font-semibold text-[#2A2C2A] font-['Work_Sans']">
                      {patient.name}
                      {patient.is_deceased && (
                        <span className="ml-2 text-xs px-2 py-0.5 bg-[#5C5F5C]/10 text-[#5C5F5C] rounded font-normal align-middle" data-testid="deceased-badge">Deceased</span>
                      )}
                    </h1>
                    <p className="text-sm sm:text-base text-[#5C5F5C] mt-1">
                      {patient.breed} • {patient.age} • {patient.weight}
                      {patient.sex && patient.sex !== 'unknown' && ` • ${patient.sex}`}
                    </p>
                    <p className="text-sm text-[#5C5F5C] mt-1">Owner: {patient.owner_name}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={startEditing}
                      className="border-[#E5E4E0] text-[#5C5F5C] hover:text-[#4B6651] whitespace-nowrap"
                      data-testid="edit-patient-btn"
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleExportPdf}
                      disabled={exportingPdf}
                      className="border-[#4B6651] text-[#4B6651] hover:bg-[#4B6651] hover:text-white whitespace-nowrap"
                      data-testid="export-pdf-btn"
                    >
                      {exportingPdf ? (
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4 mr-1" />
                      )}
                      Export PDF
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-4 p-4 bg-[#F8F7F4] rounded-lg">
              <p className="text-xs text-[#5C5F5C] uppercase tracking-wider font-semibold mb-1">
                Presenting Complaint
              </p>
              <p className="text-[#2A2C2A]">{patient.presenting_complaint}</p>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <input
                type="checkbox"
                id="deceased-toggle"
                checked={!!patient.is_deceased}
                onChange={async (e) => {
                  const isDeceased = e.target.checked;
                  try {
                    const axios = (await import('axios')).default;
                    const { BACKEND_URL } = await import('../../config/api');
                    const { data } = await axios.put(
                      `${BACKEND_URL}/api/cases/patients/${patientId}`,
                      { is_deceased: isDeceased },
                      { withCredentials: true }
                    );
                    setPatient(data);
                    const { toast } = await import('sonner');
                    toast.success(isDeceased ? 'Patient marked as deceased' : 'Patient status updated');
                  } catch (err) {
                    const { toast } = await import('sonner');
                    toast.error('Failed to update status');
                  }
                }}
                className="w-4 h-4 rounded border-[#E5E4E0] text-[#5C5F5C] focus:ring-[#4B6651]"
                data-testid="deceased-checkbox"
              />
              <label htmlFor="deceased-toggle" className="text-sm text-[#5C5F5C] cursor-pointer select-none">
                Mark patient as deceased
              </label>
            </div>
            <div className="mt-4">
              <PatientSharing patientId={patientId} />
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default PatientInfoCard;
